import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
    Grid,
    Autocomplete,
} from "@mui/material";
import { useAuthContext } from "../../../context/AuthProvider";
import { useManifiestos } from "../../../hooks/useManifest";
import { useServiceOrders } from "../../../hooks/administration/useServiceOrders";
import { fetchRouteById } from "../../../api/routesService";
import { format, parseISO, isAfter, isEqual } from "date-fns";

// Esquema de validación
const schema = yup.object().shape({
    ordsercabid: yup.number().required("Debe seleccionar una orden de servicio"),
    ordsermantra: yup.string().oneOf(["IDA", "VUELTA"]).required("Trayecto requerido"),
    manfec: yup.date().required("Debe ingresar la fecha de salida"),
    manfecfin: yup.date()
        .nullable()
        .test("fecha-valida", "La fecha de fin debe ser igual o posterior a la de salida", function (value) {
            const { manfec } = this.parent;
            return value ? isAfter(value, manfec) || isEqual(value, manfec) : true;
        }),
    proid: yup.number().required("Debe seleccionar un proyecto"),
    rutid: yup.number().required("Debe seleccionar una ruta"),
    manori: yup.string().max(255, "Máximo 255 caracteres").required("El origen es obligatorio"),
    mandes: yup.string().max(255, "Máximo 255 caracteres").required("El destino es obligatorio"),
    maninf: yup.string().max(255, "Máximo 255 caracteres"),
});

const ManifestOrderForm = ({ open, handleClose, defaultValues, orders = [] }) => {
    const isEditing = Boolean(defaultValues?.manid);
    const { user } = useAuthContext();
    const { useFetchService } = useServiceOrders();
    const { createMutation, updateMutation } = useManifiestos();
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const { data: orderDetails, isLoading: loadingDetails } = useFetchService(selectedOrderId);
    const [paradas, setParadas] = useState([]);

    // Filtrar órdenes de servicio liberadas
    /*const orders = useMemo(
        () => serviceOrders?.filter(os => ["LIBERADA", "EN_EJECUCION"].includes(os.ordsercabest)) || [],
        [serviceOrders]
      );*/

    // Valores iniciales del formulario
    const initialFormValues = useMemo(() => {
        const values = {
            ordsercabid: "",
            ordsermantra: "IDA",
            manfec: "",
            manfecfin: "",
            proid: "",
            rutid: "",
            manori: "",
            mandes: "",
            maninf: "",
        };

        if (isEditing && defaultValues) {
            values.ordsercabid = defaultValues.ordsercabid || "";
            values.ordsermantra = defaultValues.ordsermantra || "IDA";

            // Formateo seguro de fechas
            values.manfec = defaultValues.manfec
                ? format(parseISO(String(defaultValues.manfec)), "yyyy-MM-dd")
                : "";
            values.manfecfin = defaultValues.manfecfin
                ? format(parseISO(String(defaultValues.manfecfin)), "yyyy-MM-dd")
                : "";

            values.manori = defaultValues.manori || "";
            values.mandes = defaultValues.mandes || "";
            values.proid = defaultValues.proid || "";
            values.rutid = defaultValues.rutid || "";
            values.maninf = defaultValues.maninf || "";
        }

        return values;
    }, [isEditing, defaultValues]);

    const {
        control,
        handleSubmit,
        register,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: initialFormValues,
    });

    // Formatear fecha para el envío al servidor
    const formatDateOnly = useCallback((date) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        return format(date, "yyyy-MM-dd");
    }, []);

    // Obtener datos de la ruta
    const getRouteData = useCallback(async () => {
        setValue("manori", "");
        setValue("mandes", "");

        if (!orderDetails?.rutid) {
            setParadas([]);
            setValue("proid", "");
            setValue("rutid", "");
            setValue("manori", "");
            setValue("mandes", "");
            return;
        }

        try {
            const routeData = await fetchRouteById(orderDetails.rutid);
            setParadas(Array.isArray(routeData?.paradas) ? routeData.paradas : []);

            setValue("proid", orderDetails.proid);
            setValue("rutid", orderDetails.rutid);

            if (isEditing) {
                setValue("manori", initialFormValues.manori || "");
                setValue("mandes", initialFormValues.mandes || "");
            }
        } catch (error) {
            console.error("Error al obtener datos de la ruta:", error);
            setParadas([]);
            setValue("proid", "");
            setValue("rutid", "");
            setValue("manori", "");
            setValue("mandes", "");
        }
    }, [orderDetails, setValue, isEditing, initialFormValues]);

    // Efecto para resetear el formulario cuando se abre/cierra
    useEffect(() => {
        if (open) {
            reset(initialFormValues);
            if (isEditing && initialFormValues.ordsercabid) {
                setSelectedOrderId(Number(initialFormValues.ordsercabid));
            } else if (!isEditing) {
                setSelectedOrderId(null);
            }
        } else {
            setSelectedOrderId(null);
            setParadas([]);
        }
    }, [open, initialFormValues, reset, isEditing]);

    // Efecto para obtener datos de la ruta cuando cambian los detalles de la orden
    useEffect(() => {
        if (open && orderDetails) {
            getRouteData();
        }
    }, [open, orderDetails, getRouteData]);

    // Manejar envío del formulario
    const onSubmit = useCallback((data) => {
        if (!orderDetails) return;

        const payload = {
            manifiesto: {
                maninf: data.maninf,
                manfec: formatDateOnly(data.manfec),
                manfecfin: formatDateOnly(data.manfecfin),
                manori: data.manori,
                mandes: data.mandes,
                proid: orderDetails.proid,
                rutid: orderDetails.rutid,
                crebyid: user.EmpID,
                ...(isEditing && { updbyid: user.EmpID }),
            },
            manifiestoOrden: {
                ordsercabid: orderDetails.ordsercabid,
                ordsermantra: data.ordsermantra,
            },
        };

        if (isEditing) {
            updateMutation.mutate(
                { id: defaultValues.manid, data: payload.manifiesto },
                { onSuccess: handleClose }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: handleClose,
                onError: (error) => {
                    console.error("Error al crear manifiesto:", error);
                },
            });
        }
    }, [orderDetails, formatDateOnly, user, isEditing, defaultValues, updateMutation, createMutation, handleClose]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                {isEditing
                    ? `Editar Manifiesto MN-${String(defaultValues?.manid).padStart(6, "0")}`
                    : "Crear Manifiesto"
                }
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>                            
                            <Controller
                                name="ordsercabid"
                                control={control}
                                render={({ field }) => {
                                    const baseOrders = orders || [];

                                    // opción seleccionada actual (por ID)
                                    let selectedOption =
                                        baseOrders.find(o => String(o.ordsercabid) === String(field.value)) || null;

                                    // caso edición: el ID existe en defaultValues pero aún no está en orders
                                    let optionsList = baseOrders;
                                    if (isEditing && defaultValues?.ordsercabid && !selectedOption) {
                                        const fallback = {
                                            ordsercabid: defaultValues.ordsercabid,
                                            ordsercabnom: `Cargando Orden #${defaultValues.ordsercabid}...`,
                                        };
                                        optionsList = [fallback, ...baseOrders];
                                        selectedOption = fallback;
                                    }

                                    return (
                                        <Autocomplete
                                            disabled={isEditing}
                                            options={optionsList}
                                            value={selectedOption}
                                            onChange={(_, newValue) => {
                                                const id = newValue ? newValue.ordsercabid : '';
                                                field.onChange(id);
                                                setSelectedOrderId(newValue ? Number(newValue.ordsercabid) : null);
                                            }}
                                            getOptionLabel={(option) =>
                                                option
                                                    ? `OS-${String(option.ordsercabid).padStart(6, '0')} ${option.ordsercabnom ?? ''}`
                                                    : ''
                                            }
                                            // 🔍 filtro por ID o por nombre
                                            filterOptions={(options, { inputValue }) => {
                                                const term = inputValue.trim().toLowerCase();
                                                if (!term) return options;
                                                return options.filter(o =>
                                                    String(o.ordsercabid).includes(term) ||
                                                    (o.ordsercabnom || '').toLowerCase().includes(term)
                                                );
                                            }}
                                            loading={loadingDetails}
                                            loadingText="Cargando órdenes..."
                                            noOptionsText="No hay órdenes liberadas"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Orden de Servicio"
                                                    fullWidth
                                                    margin="normal"
                                                    error={!!errors.ordsercabid}
                                                    helperText={errors.ordsercabid?.message}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        {orderDetails && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Proyecto"
                                    value={orderDetails.pronom}                                    
                                    fullWidth
                                    disabled={isEditing}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Controller
                                name="ordsermantra"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Trayecto"
                                        fullWidth
                                        disabled={isEditing}
                                        error={!!errors.ordsermantra}
                                        helperText={errors.ordsermantra?.message}
                                    >
                                        <MenuItem value="IDA">SUBIDA</MenuItem>
                                        <MenuItem value="VUELTA">BAJADA</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Descripción"
                                fullWidth
                                margin="normal"
                                {...register("maninf")}
                                error={!!errors.maninf}
                                helperText={errors.maninf?.message}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="manori"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Origen"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.manori}
                                        helperText={errors.manori?.message}
                                    >
                                        <MenuItem value="">Sin selección</MenuItem>
                                        {isEditing && initialFormValues.manori && !paradas.find(p => p.locnom === initialFormValues.manori) && (
                                            <MenuItem value={initialFormValues.manori}>
                                                {initialFormValues.manori} (Cargando opciones...)
                                            </MenuItem>
                                        )}
                                        {paradas.map((parada) => (
                                            <MenuItem key={parada.locid} value={parada.locnom}>
                                                {parada.locnom}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Controller
                                name="mandes"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Destino"
                                        fullWidth
                                        margin="normal"
                                        error={!!errors.mandes}
                                        helperText={errors.mandes?.message}
                                    >
                                        <MenuItem value="">Sin selección</MenuItem>
                                        {isEditing && initialFormValues.mandes && !paradas.find(p => p.locnom === initialFormValues.mandes) && (
                                            <MenuItem value={initialFormValues.mandes}>
                                                {initialFormValues.mandes} (Cargando opciones...)
                                            </MenuItem>
                                        )}
                                        {paradas.map((parada) => (
                                            <MenuItem key={parada.locid} value={parada.locnom}>
                                                {parada.locnom}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Fecha de Salida"
                                type="date"
                                fullWidth
                                margin="normal"
                                {...register('manfec')}
                                error={!!errors.manfec}
                                helperText={errors.manfec?.message}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Fecha de Fin"
                                type="date"
                                fullWidth
                                margin="normal"
                                {...register("manfecfin")}
                                error={!!errors.manfecfin}
                                helperText={errors.manfecfin?.message}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={createMutation.isLoading || updateMutation.isLoading}
                    >
                        {createMutation.isLoading || updateMutation.isLoading ? (
                            <CircularProgress size={24} />
                        ) : "Guardar"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ManifestOrderForm;